import { theme } from "@chakra-ui/core";
import { css } from "@emotion/core";

import { fonts, colors } from "./tailwind";

// Used so hover colors get automatically updated
const brandPrimaryColor = "#0A2240"; // navy
const brandSecondaryColor = "#60a60e"; // green
const brandTertiaryColor = "#b0b5c0"; // grey
const brandQuaternaryColor = "#0061a7"; // light blue
const brandBlackColor = "#444444"; // black
const brandGreyColor = "#e7e9ec"; // grey
const brandWhiteColor = "#FFFFFF";

// [container, breakpoint]
const sizes = {
    sm: ["520px", "576px"],
    md: ["720px", "768px"],
    lg: ["960px", "992px"],
    xl: ["1170px", "1200px"],
};

const breakpoints = Object.values(sizes).reduce((bp, size) => {
    bp.push(size[1]);
    return bp;
}, []);

breakpoints.sm = breakpoints[0];
breakpoints.md = breakpoints[1];
breakpoints.lg = breakpoints[2];
breakpoints.xl = breakpoints[3];

export const EditorTheme = {
    ...theme,
    breakpoints,
    colors: {
        ...theme.colors,
        brand: {
            black: brandBlackColor,
            white: brandWhiteColor,
            grey: brandGreyColor,
            lightGrey: "#f3f4f6",
            primary: brandPrimaryColor,
            secondary: brandSecondaryColor,
            tertiary: brandTertiaryColor,
            quaternary: brandQuaternaryColor,
        },
    },
    fonts: {
        ...theme.fonts,
        heading: "Mulish, sans-serif",
        body: "Mulish, sans-serif",
    },
};

export const AdminTheme = {
    breakpoints: {
        mobileS: 320,
        mobileM: 375,
        mobileL: 425,
        tablet: 768,
        laptop: 992,
        desktop: 1024,
        desktopL: 1700,
    },
};

// Global styles designs: link to the client's style guide if there is one.

export const GlobalStyles = css`
    /* HEADING AND TEXT */
    body {
        font-family: ${fonts.body};
        color: ${colors["brand-black"]};
    }

    /* ADMIN CONTENT BLOCK */
    .ory-row {
        h1 {
            font-family: ${fonts.heading};
            font-weight: bold;
            font-size: 30px;
            line-height: 34px;
        }

        h2 {
            font-family: ${fonts.heading};
            font-weight: bold;
            font-size: 24px;
            line-height: 28px;
        }

        h3 {
            font-family: ${fonts.heading};
            font-weight: bold;
            font-size: 20px;
            line-height: 24px;
        }

        h4 {
            font-family: ${fonts.heading};
            font-weight: bold;
            font-size: 16px;
            line-height: 20px;
        }

        h5 {
            font-family: ${fonts.heading};
            font-weight: bold;
            font-size: 14px;
            line-height: 18px;
        }

        h6 {
            font-family: ${fonts.heading};
            font-weight: bold;
            font-size: 12px;
            line-height: 16px;
        }

        p {
            font-family: ${fonts.body};
            font-size: 16px;
            line-height: 24px;
        }

        .text-small {
            font-family: ${fonts.body};
            font-size: 14px;
            line-height: 20px;
        }

        .text-tiny {
            font-family: ${fonts.body};
            font-size: 12px;
            line-height: 18px;
        }

        blockquote {
            font-size: 18px;
            line-height: 26px;
            padding-left: 30px;
            border-left: 4px solid ${colors["brand-primary"]};
        }

        /* LIST */
        ul:not([class^="DayPicker"]) {
            list-style: none;
            margin-left: 0;
            margin-right: 0;
            margin-bottom: 20px;
            position: relative;

            li {
                line-height: 30px;
                padding-left: 20px;
                position: relative;
                margin-bottom: 5px;

                &:last-of-type {
                    margin-bottom: 0;
                }

                &:before {
                    display: block;
                    position: absolute;
                    left: 0;
                    height: 8px;
                    width: 8px;
                    border-radius: 9999px;
                    background-color: ${colors["brand-grey"]};
                    content: "";
                    top: 12px;
                    margin-right: 10px;
                }
            }
        }

        ol:not(.breadcrumb) {
            list-style: none;
            margin-left: 0;
            margin-right: 0;
            margin-bottom: 20px;
            position: relative;
            padding-left: 0;
            counter-reset: list-number;
            font-size: 16px;
            line-height: 24px;

            li {
                margin-bottom: 15px;
                position: relative;
                display: flex;
                counter-increment: list-number;
                &:before {
                    display: block;
                    content: "";
                    margin-right: 5px;
                    content: counter(list-number) ".";
                }

                &:last-of-type {
                    margin-bottom: 0;
                }
            }
        }

        /* TABLE */
        .table {
            td {
                vertical-align: top;
                padding: 15px;
            }
        }

        .js-focus-visible :focus:not([data-focus-visible-added]) {
            outline: none;
            box-shadow: none;
        }

        a:not([type^="button"]) {
            text-decoration: underline;
            color: ${colors["brand-primary"]};

            &:hover {
                color: ${colors["brand-primary-darken"]};
            }

            &:visited {
                color: #3f05c2;
            }
        }

        a[type^="button"] {
            text-decoration: none;

            &:hover {
                text-decoration: none;
            }
        }

        form {
            textarea,
            input {
                margin-bottom: 15px;
            }
        }

        .content__text {
            h1,
            h2,
            h3,
            h4,
            h5,
            h6 {
                margin-bottom: 10px;
            }
        }

        figure.table {
            width: 100%;

            table {
                margin: 15px 0;
                min-width: 100%;
                font-size: 14px;
                line-height: 20px;

                tbody {
                    > tr {
                        &:first-of-type {
                            font-weight: bold;
                            font-size: 16px;
                            background-color: ${colors[
                                "brand-dark-grey"
                            ]} !important;
                            color: ${colors["brand-white"]};
                            border-radius: 13px 13px 0 0;
                            border-top: none;

                            td {
                                border-top: none;

                                &:first-of-type {
                                    border-top-left-radius: 13px;
                                }

                                &:last-of-type {
                                    border-top-right-radius: 13px;
                                }
                            }
                        }

                        &:last-of-type {
                            border-radius: 0 0 13px 13px;
                            border-bottom: none;

                            td {
                                border-bottom: none;

                                &:first-of-type {
                                    border-bottom-left-radius: 13px;
                                }

                                &:last-of-type {
                                    border-bottom-right-radius: 13px;
                                }
                            }
                        }

                        &:nth-child(odd) {
                            background-color: ${colors["brand-light-grey"]};
                        }

                        &:nth-child(even) {
                            background-color: transparent;
                        }
                    }

                    tr {
                        td {
                            padding: 15px;
                        }
                    }
                }
            }
        }
    }
`;
